import React, { useContext, useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "./firebase";
import useUserData from "../hooks/useUserData";
import { SiteStateContext } from "./SiteStateContext";

const AdminPanel = () => {
  const { userData, loading } = useUserData();
  const navigate = useNavigate();
  const {
    impersonateTeamId,
    setImpersonateTeamId,
    impersonateTeamName,
    setImpersonateTeamName,
    isImpersonating
  } = useContext(SiteStateContext);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(impersonateTeamId || "");
  const [teamSearch, setTeamSearch] = useState("");
  const [loadingTeams, setLoadingTeams] = useState(true);

  useEffect(() => {
    if (!loading && (!userData || !userData.adminUser)) {
      navigate("/starsonata/team");
    }
  }, [loading, userData, navigate]);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const snap = await getDocs(collection(db, "teams"));
        const rows = snap.docs.map((docSnap) => {
          const data = docSnap.data();
          const teamId = data.teamId || data.teamID || docSnap.id;
          const name = data.name || data.teamName || data.teamID || docSnap.id;
          return { teamId, name };
        });
        rows.sort((a, b) => a.name.localeCompare(b.name));
        setTeams(rows);
      } catch (err) {
        console.error("Failed to load teams:", err);
        setTeams([]);
      } finally {
        setLoadingTeams(false);
      }
    };

    if (userData?.adminUser) {
      loadTeams();
    }
  }, [userData?.adminUser]);

  const filteredTeams = useMemo(() => {
    const query = teamSearch.trim().toLowerCase();
    if (!query) return teams;
    return teams.filter((team) => {
      const name = String(team.name || "").toLowerCase();
      const id = String(team.teamId || "").toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  }, [teams, teamSearch]);

  const selectedTeam = useMemo(
    () => teams.find((team) => team.teamId === selectedTeamId),
    [teams, selectedTeamId]
  );

  const handleApply = () => {
    if (!selectedTeam) return;
    setImpersonateTeamId(selectedTeam.teamId);
    setImpersonateTeamName(selectedTeam.name);
    navigate("/starsonata/team");
  };

  const handleClear = () => {
    setImpersonateTeamId("");
    setImpersonateTeamName("");
  };

  if (loading || !userData) return <p>Loading admin tools...</p>;

  if (!userData.adminUser) {
    return <p>Access denied.</p>;
  }

  return (
    <div style={styles.container}>
      <h1 style={{ color: "var(--theme-text-color)" }}>Admin Panel</h1>
      <p style={{ color: "var(--theme-text-color)", marginTop: "0.5rem" }}>
        Use this to view the site as another team for debugging.
      </p>

      <div style={styles.card}>
        <label style={styles.label} htmlFor="teamSearch">
          Find team
        </label>
        <input
          id="teamSearch"
          type="text"
          value={teamSearch}
          onChange={(e) => setTeamSearch(e.target.value)}
          placeholder="Type team name or ID..."
          style={styles.searchInput} />
        
        <label style={{ ...styles.label, marginTop: "0.75rem" }} htmlFor="teamSelect">
          Select team
        </label>
        <select
          id="teamSelect"
          value={selectedTeamId}
          onChange={(e) => setSelectedTeamId(e.target.value)}
          style={styles.select}
          disabled={loadingTeams}>
          
          <option value="">-- Choose a team --</option>
          {filteredTeams.map((team) =>
          <option key={team.teamId} value={team.teamId}>
              {team.name} ({team.teamId})
            </option>
          )}
        </select>
        {filteredTeams.length === 0 &&
        <div style={styles.emptyState}>No teams match that search.</div>
        }

        <div style={styles.actions}>
          <button style={styles.primaryButton} onClick={handleApply} disabled={!selectedTeam}>
            View as team
          </button>
          <button style={styles.secondaryButton} onClick={handleClear} disabled={!isImpersonating}>
            Stop impersonating
          </button>
        </div>

        <div style={styles.status}>
          <strong>Status:</strong>{" "}
          {isImpersonating ?
          `Impersonating ${impersonateTeamName || impersonateTeamId}` :
          "Not impersonating"}
        </div>
      </div>
    </div>);

};

const styles = {
  container: {
    padding: "2rem",
    textAlign: "center"
  },
  card: {
    margin: "1.5rem auto 0",
    maxWidth: "520px",
    backgroundColor: "var(--panelBg)",
    border: "1px solid var(--teamColor)",
    borderRadius: "10px",
    padding: "1.5rem"
  },
  label: {
    display: "block",
    color: "var(--theme-text-color)",
    fontWeight: 600,
    marginBottom: "0.5rem",
    textAlign: "left"
  },
  select: {
    width: "100%",
    padding: "0.6rem 0.75rem",
    borderRadius: "8px",
    border: "1px solid #444",
    backgroundColor: "#1e1e1e",
    color: "var(--theme-text-color)"
  },
  searchInput: {
    width: "100%",
    padding: "0.6rem 0.75rem",
    borderRadius: "8px",
    border: "1px solid #444",
    backgroundColor: "#1e1e1e",
    color: "var(--theme-text-color)"
  },
  actions: {
    display: "flex",
    gap: "0.75rem",
    justifyContent: "center",
    marginTop: "1rem"
  },
  primaryButton: {
    padding: "0.6rem 1rem",
    backgroundColor: "var(--theme-color)",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  secondaryButton: {
    padding: "0.6rem 1rem",
    backgroundColor: "#444",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  status: {
    marginTop: "1rem",
    color: "var(--theme-text-color)"
  },
  emptyState: {
    marginTop: "0.75rem",
    color: "var(--textMuted)"
  }
};

export default AdminPanel;